<template>
  <div class="policy-wrapper" :class="[
    `policy-wrapper--${policyStatusColor}`,
    { 'policy-wrapper--expanded': showMoreDetails },
  ]">
    <div class="policy" :class="{ 'policy--expanded': showMoreDetails }">
      <div class="row row--policy box box--default" :class="{ 'row--active': showMoreDetails }">
        <div class="row__data row__data--medium row__data--fixed"
          :class="{ 'row__data--underline': $checkRolePermission('policy_accident_edit') }">
          <span v-if="$checkRolePermission('policy_accident_edit')" @click="showStatusPopup = true"
            style="cursor: pointer">
            <transition name="fade" mode="in-out">
              <PopupEditer v-if="showStatusPopup" @togglePopup="showStatusPopup = $event" :items="policyStatuses"
                :selected="policyStatus" :label="'name'" @submit="updatePolicyStatus($event)" />
            </transition>
            {{ policyStatus }}
          </span>

          <span v-else>
            {{ policyStatus }}
          </span>
        </div>

        <div class="row__data row__data--medium">{{ policyNumber }}</div>
        <div class="row__data row__data--large policy__company">
          <span>{{ company }}</span>
        </div>
        <div class="row__data row__data--medium">
          {{ branch }}
        </div>
        <div class="row__data row__data--medium">{{ damageFrom }}</div>
        <div class="row__data row__data--medium">{{ damageTo }}</div>
        <div class="row__data row__data--medium">{{ price }}</div>
        <div v-if="$checkRolePermission('policy_accident_create')"
          class="row__data row__data--medium row__data--add-accident icon__add--active" @click="showAccidentModal()">
        </div>
        <div v-else style="cursor:no-drop;"
          class="row__data row__data--medium row__data--add-accident icon__add--active"></div>
        <div class="row__data row__data--medium row__data--half row__data--last">
          <div class="row__data row__data--small" :class="[
            { 'row__data--rotate': showMoreDetails },
            { 'row__data--more': policy.accidents.length },
          ]" @click="toggleMoreDetails()"></div>
        </div>
      </div>
    </div>
    <div class="policy-wrapper__accidents" ref="accidentsRef" :style="{
      maxHeight: showMoreDetails ? `${containerMaxHeight}px` : '0px',
    }">
      <AccidentsTable :accidents="accidents" :openPolicyFilesModal="openPolicyFilesModal" />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
//import moment from "moment";
import statics from "@/statics";
import { formatDisplayDate } from "@/helpers/dateFormatters";
import { fetchGlobalCustomer } from "@/helpers/accountDetailHelpers";
import PopupEditer from "@/partials/PopupEditer";
import AccidentsTable from "@/components/AccountDetails/AccidentsTable";

defineOptions({
  name: "AccidentsPolicy"
});

const props = defineProps({
  policy: {
    required: true,
    type: Object,
  },
});

const store = useStore();
const route = useRoute();

// Refs
const accidentsRef = ref(null);

// State
const showMoreDetails = ref(false);
const showStatusPopup = ref(false);
const acceptedId = 2;
const declinedId = 3;
const containerMaxHeight = ref(0);
const bottomOffsetMargin = 10;
const accidentHeight = 66;
const policyLoading = ref(false);

// Computed
const customer = computed(() => store.getters.customer);
const policyForms = computed(() => store.getters.policyForms);

const policyStatus = computed(() => props.policy.policy_status?.name ?? statics.empty);
const policyNumber = computed(() => props.policy.policy_number ?? statics.empty);
const company = computed(() => props.policy.company?.full_name ?? statics.empty);

const branch = computed(() =>
  props.policy.policy_services.length
    ? [...new Set(props.policy.policy_services.map(s => s.service?.branch_category?.long_de ?? ""))].join(" / ")
    : statics.empty
);

const policyStatusColor = computed(() => {
  const status = statics.policyStatuses.find(s => props.policy.policy_status?.id === s.id);
  return status ? status.name : null;
});

const policyStatuses = computed(() => policyForms.value["policy-statuses"]);

const damageFrom = computed(() =>
  props.policy.accidents.length ? formatDisplayDate(props.policy.accidents[0].date) : statics.empty
);

const damageTo = computed(() => {
  if (props.policy.accidents.length) {
    const lastAccident = props.policy.accidents[props.policy.accidents.length - 1];
    if (lastAccident.accident_status.id === acceptedId || lastAccident.accident_status.id === declinedId) {
      return formatDisplayDate(lastAccident.date);
    }
  }
  return statics.empty;
});

const price = computed(() => {
  if (props.policy.accidents.length) {
    const lastAccident = props.policy.accidents[props.policy.accidents.length - 1];
    if (lastAccident.accident_status.id === acceptedId || lastAccident.accident_status.id === declinedId) {
      return lastAccident.price;
    }
  }
  return statics.empty;
});

const accidents = computed(() =>
  props.policy.accidents.length
    ? [...props.policy.accidents].sort((a, b) => (a.accident_status.id < b.accident_status.id ? -1 : 1))
    : []
);

// Methods
const calculateContainerMaxHeight = async () => {
  await nextTick();
  if (!accidentsRef.value) return;

  const childrenHeight = [...accidentsRef.value.children].reduce((acc, child) => acc + child.clientHeight, 0);
  const accidentTotalHeight = props.policy.accidents.length * accidentHeight;
  const childrenAccidentHeight = props.policy.accidents.flatMap(a => a.childrens).length * accidentHeight;

  containerMaxHeight.value = childrenHeight + accidentTotalHeight + childrenAccidentHeight + bottomOffsetMargin;
};

const updatePolicyStatus = async (policyStatusId) => {
  policyLoading.value = true;
  await store.dispatch("updatePolicyStatus", {
    policyId: props.policy.id,
    policyStatusId,
  });
  await fetchGlobalCustomer(route.params.id);
  showStatusPopup.value = false;
  policyLoading.value = false;
};

const toggleMoreDetails = () => {
  if (props.policy.accidents.length) {
    showMoreDetails.value = !showMoreDetails.value;
  }
};

const showAccidentModal = () => {
  store.commit("setAccidentPolicyId", props.policy.id);
  store.commit("setShowAddAccidentModal", true);
};

const openPolicyFilesModal = (id, files, type) => {
  store.commit("setPolicyFileId", id);
  store.commit("setPolicyFiles", files);
  if (type === "accident") store.commit("setPolicyFilesModalIsAccident", true);
  else if (type === "accidentChildren") store.commit("setPolicyFilesModalIsAccidentChildren", true);
  store.commit("setShowPolicyFilesModal", true);
};

// Watchers & Lifecycle
watch(customer, () => {
  calculateContainerMaxHeight();
  if (!props.policy.accidents.length) {
    showMoreDetails.value = false;
  }
});

onMounted(calculateContainerMaxHeight);
</script>

<style lang="scss" scoped>
.policy-wrapper {
  border-radius: var(--radius-1);
  margin-bottom: 25px;
}

.policy {
  margin-bottom: 0;
}
</style>
